<template>
  <div>
    <div v-if="loading" class="banner-skeleton">
      <q-skeleton type="rect" height="240px" animation="fade" />
    </div>
    <div v-else-if="error" class="banner-error q-pa-md text-center">
      <q-icon name="error_outline" color="negative" size="sm" class="q-mr-sm" />
      <span>{{ error }}</span>
    </div>    <div v-else-if="!banners || (Array.isArray(banners) && banners.length === 0)" class="no-banners-placeholder bg-grey-2 q-pa-lg text-center" style="height: 240px">
      <q-icon name="image" size="4rem" color="grey-6" />
      <div class="text-h6 text-grey-7 q-mt-md">{{ type === 'main' ? 'Main Banner' : 'Side Banner' }}</div>
      <p class="text-grey-7">Add a banner from admin panel</p>
    </div>
    <div v-else>      <q-carousel
        v-if="Array.isArray(banners) && banners.length > 0"
        v-model="currentSlide"
        navigation
        arrows
        infinite
        autoplay
        :autoplay-timeout="8000"
        @mouseenter="pauseAutoplay"
        @mouseleave="resumeAutoplay"        :class="type === 'main' ? 'main-slider' : 'side-slider'"
        style="position: relative; min-height: 240px; height: 240px; overflow: visible; z-index: 1;"
      >
        <div class="banner-type-indicator" :class="type === 'main' ? 'main-indicator' : 'side-indicator'">
          {{ type === 'main' ? 'Featured' : 'Special' }}
        </div>
        <q-carousel-slide
          v-for="(banner, index) in banners"
          :key="index"
          :name="index"
          :class="type === 'main' ? 'main-carousel-slide' : 'side-carousel-slide'"
          style="position: relative; min-height: 240px; height: 240px; overflow: visible;"
        >
          <q-img
            :src="banner.banner_image_url"
            :alt="banner.title"
            fit="contain"
            height="240px"
            width="100%"
            @click="emitBannerClick(banner)"
            @error="emitImgError"
            :class="'cursor-pointer carousel-image ' + (type === 'main' ? 'main-banner-img' : 'side-banner-img')"
            style="position: relative; z-index: 5; min-height: 240px;"
          />
        </q-carousel-slide>
        <template #control>
          <div v-if="banners.length > 1" class="banner-carousel-dots">
            <span
              v-for="(banner, idx) in banners"
              :key="'dot-' + idx"
              class="banner-dot"
              :class="{ active: currentSlide === idx }"
              tabindex="0"
              role="button"
              :aria-label="'Go to banner ' + (idx + 1)"
              @click.stop="currentSlide = idx"
              @keydown.enter.space="currentSlide = idx"
            ></span>
          </div>
        </template>
      </q-carousel>      <q-img
        v-else
        :src="banners.banner_image_url"
        :alt="banners.title"
        height="240px"
        width="100%"
        fit="contain"
        @click="emitBannerClick(banners)"
        @error="emitImgError"
        :class="'cursor-pointer carousel-image ' + (type === 'main' ? 'main-banner-img' : 'side-banner-img')"
        style="position: relative; z-index: 5; min-height: 240px;"
      >
        <div class="banner-type-indicator" :class="type === 'main' ? 'main-indicator' : 'side-indicator'">
          {{ type === 'main' ? 'Featured' : 'Special' }}
        </div>
      </q-img>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, toRefs } from 'vue';
const props = defineProps({
  banners: { type: [Array, Object], default: () => [] },
  loading: Boolean,
  error: String,
  type: { type: String, default: 'main' },
});
const emit = defineEmits(['banner-click', 'img-error']);
const { banners } = toRefs(props);
const currentSlide = ref(0);

watch(banners, (val) => {
  if (Array.isArray(val) && val.length > 0) currentSlide.value = 0;
});

const pauseAutoplay = () => {
  // No-op: handled by parent if needed
};
const resumeAutoplay = () => {
  // No-op: handled by parent if needed
};
const emitBannerClick = (banner) => emit('banner-click', banner);
const emitImgError = (e) => emit('img-error', e);
</script>

<style lang="scss" scoped>
/* Banner styling */
.banner-skeleton {
  border-radius: 16px;
  overflow: hidden;
}

.banner-error {
  background-color: rgba(244, 67, 54, 0.05);
  color: #f44336;
  border-radius: 12px;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.no-banners-placeholder {
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

/* Main carousel styling */
.main-slider {
  border-radius: 12px;
  overflow: visible !important;
  background-color: white !important;
  position: relative;
  min-height: 240px;
  height: 240px !important;
  z-index: 1;
  box-shadow: 0 10px 25px rgba(255, 152, 0, 0.09), 0 8px 10px rgba(0, 0, 0, 0.03);
  border: 2px solid rgba(255, 152, 0, 0.1);
}

.side-slider {
  border-radius: 16px;
  overflow: visible !important;
  background-color: white !important;
  position: relative;
  min-height: 240px;
  height: 240px !important;
  z-index: 1;
  box-shadow: 0 8px 20px rgba(33, 150, 243, 0.07), 0 6px 8px rgba(0, 0, 0, 0.02);
  border: 2px solid rgba(33, 150, 243, 0.1);
}

.carousel-image {
  background-position: center;
  position: relative !important;
  z-index: 5;
  min-height: 240px;
  height: 100% !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  
  :deep(.q-img__content) {
    background-color: white !important;
    position: absolute !important;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 4;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  :deep(.q-img__image) {
    object-fit: contain !important;
    background-color: white !important;
    max-width: 90% !important;
    max-height: 90% !important;
    margin: auto !important;
    position: relative !important;
    z-index: 5;
    visibility: visible !important;
    opacity: 1 !important;
    transition: none !important;
  }
}

/* Fix fade animation issues */
:deep(.q-carousel__slide) {
  transform: none !important; /* Prevent transform animations that might hide content */
  opacity: 1 !important; /* Keep slide visible */
  transition: none !important; /* Disable transitions that might cause flickering */
}

:deep(.q-carousel) {
  min-height: 240px !important;
  height: 240px !important;
}

/* Prevent image from disappearing */
:deep(.q-img) {
  opacity: 1 !important;
  visibility: visible !important;
  position: relative !important;
  z-index: 5 !important;
  transition: none !important;
}

/* Carousel container */
.carousel-container {
  border-radius: 8px;
  overflow: visible !important; /* Changed from hidden to visible */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background-color: white !important;
  position: relative;
  z-index: 2;  min-height: 240px;
}

/* Carousel control dots */
.banner-carousel-dots {
  position: absolute;
  left: 50%;
  bottom: 12px;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  gap: 8px;
  align-items: center;
  background: rgba(0,0,0,0.2);
  padding: 5px 12px;
  border-radius: 20px;
}

.banner-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #e0e0e0;
  border: none;
  cursor: pointer;
  transition: background 0.18s;
  display: block;
}

.banner-dot.active {
  background: #ff9800;
  width: 12px;
  height: 12px;
}

/* Add banner type indicators */
.banner-type-indicator {
  position: absolute;
  top: 10px;
  right: 10px;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  z-index: 10;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.main-indicator {
  background: linear-gradient(135deg, #ff9800 0%, #ff5722 100%);
  color: white;
  box-shadow: 0 3px 10px rgba(255, 152, 0, 0.25);
}

.side-indicator {
  background: linear-gradient(135deg, #2196f3 0%, #03a9f4 100%);
  color: white;
  box-shadow: 0 3px 10px rgba(33, 150, 243, 0.25);
}

/* Responsive design */
@media (max-width: 1200px) {
  .main-slider, 
  .side-slider,
  .main-banner-img,
  .side-banner-img,
  .no-banners-placeholder,
  .banner-skeleton .q-skeleton,
  :deep(.q-carousel),
  :deep(.q-carousel__slide),
  :deep(.q-img) {    height: 240px !important;
    min-height: 240px !important;
    max-height: 240px !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
}

@media (max-width: 767px) {
  .main-slider, 
  .side-slider,
  .main-banner-img,
  .side-banner-img,
  .no-banners-placeholder,
  .banner-skeleton .q-skeleton,
  :deep(.q-carousel),
  :deep(.q-carousel__slide),
  :deep(.q-img) {
    height: 220px !important;
    min-height: 220px !important;
    max-height: 220px !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
}
</style>
